import React, { useState } from 'react';
import patientService, { PatientCreate, Medication, Patient, PatientUpdate, ProgressNote } from '../../services/patientService';
import './Patients.css';

interface PatientFormProps {
  onPatientAdded: () => void;
  onCancel: () => void;
  patient?: Patient; // If provided, we're in edit mode
  isEdit?: boolean;
}

const PatientForm: React.FC<PatientFormProps> = ({ onPatientAdded, onCancel, patient, isEdit = false }) => {
  const [firstName, setFirstName] = useState<string>(patient?.first_name || '');
  const [lastName, setLastName] = useState<string>(patient?.last_name || '');
  const [dateOfBirth, setDateOfBirth] = useState<string>(patient?.date_of_birth ? new Date(patient.date_of_birth).toISOString().split('T')[0] : '');
  const [chronicConditions, setChronicConditions] = useState<string>(patient?.chronic_conditions || '');
  const [medications, setMedications] = useState<Medication[]>(
    patient?.medications?.length ? patient.medications : [{ name: '', dosage: '', frequency: '' }]
  );
  const [progressNotes, setProgressNotes] = useState<ProgressNote[]>(
    patient?.progress_notes?.length ? patient.progress_notes.map(note => ({
      ...note,
      date: note.date ? new Date(note.date).toISOString().split('T')[0] : ''
    })) : []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [editProgressNotes, setEditProgressNotes] = useState<boolean>(false);

  const handleAddMedication = () => {
    setMedications([...medications, { name: '', dosage: '', frequency: '' }]);
  };

  const handleAddProgressNote = () => {
    const today = new Date().toISOString().split('T')[0];
    setProgressNotes([...progressNotes, { date: today, content: '' }]);
  };

  const handleRemoveMedication = (index: number) => {
    const updatedMedications = [...medications];
    updatedMedications.splice(index, 1);
    setMedications(updatedMedications);
  };

  const handleRemoveProgressNote = (index: number) => {
    const updatedProgressNotes = [...progressNotes];
    updatedProgressNotes.splice(index, 1);
    setProgressNotes(updatedProgressNotes);
  };

  const handleMedicationChange = (index: number, field: keyof Medication, value: string) => {
    const updatedMedications = [...medications];
    updatedMedications[index][field] = value;
    setMedications(updatedMedications);
  };

  const handleProgressNoteChange = (index: number, field: keyof ProgressNote, value: string) => {
    const updatedProgressNotes = [...progressNotes];
    updatedProgressNotes[index][field] = value;
    setProgressNotes(updatedProgressNotes);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!firstName.trim() || !lastName.trim() || !dateOfBirth) {
      setError('Please fill in all required fields');
      return;
    }

    // Filter out empty medications
    const filteredMedications = medications.filter(med => med.name.trim() !== '');

    // Filter out progress notes with empty content
    const filteredProgressNotes = progressNotes.filter(note => note.content.trim() !== '');
    
    // Format progress notes with proper date handling
    const formattedProgressNotes = filteredProgressNotes.map(note => ({
      content: note.content,
      // Ensure date is in ISO format (YYYY-MM-DD)
      date: note.date ? note.date : new Date().toISOString().split('T')[0]
    }));

    const patientData: PatientCreate | PatientUpdate = {
      first_name: firstName,
      last_name: lastName,
      // Ensure date_of_birth is in ISO format (YYYY-MM-DD)
      date_of_birth: dateOfBirth,
      chronic_conditions: chronicConditions.trim() || undefined,
      medications: filteredMedications.length > 0 ? filteredMedications : undefined,
      progress_notes: formattedProgressNotes.length > 0 ? formattedProgressNotes : undefined
    };

    try {
      setLoading(true);
      if (isEdit && patient?.id) {
        await patientService.updatePatient(patient.id, patientData);
      } else {
        await patientService.createPatient(patientData as PatientCreate);
      }
      onPatientAdded();
      setError(null);
    } catch (err) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} patient:`, err);
      setError(`Failed to ${isEdit ? 'update' : 'create'} patient. Please try again.`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!patient?.id) return;
    
    try {
      setLoading(true);
      await patientService.deletePatient(patient.id);
      onPatientAdded(); // This will refresh the patient list
    } catch (err) {
      console.error('Error deleting patient:', err);
      setError('Failed to delete patient. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <div className="patient-form-container">
      <h2>{isEdit ? 'Edit Patient' : 'Add New Patient'}</h2>
      {error && <div className="patient-form-error">{error}</div>}
      <form onSubmit={handleSubmit} className="patient-form">
        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="dateOfBirth">Date of Birth *</label>
          <input
            type="date"
            id="dateOfBirth"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="chronicConditions">Chronic Conditions</label>
          <textarea
            id="chronicConditions"
            value={chronicConditions}
            onChange={(e) => setChronicConditions(e.target.value)}
            rows={3}
          />
        </div>
        
        <div className="medications-section">
          <h3>Medications</h3>
          {medications.map((medication, index) => (
            <div key={index} className="medication-item">
              <div className="medication-fields">
                <input
                  type="text"
                  placeholder="Medication name"
                  value={medication.name}
                  onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Dosage"
                  value={medication.dosage}
                  onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Frequency"
                  value={medication.frequency}
                  onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                />
                <button 
                  type="button" 
                  className="remove-medication-btn"
                  onClick={() => handleRemoveMedication(index)}
                  title="Remove medication"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
          <button 
            type="button" 
            className="add-medication-btn"
            onClick={handleAddMedication}
          >
            Add Medication
          </button>
        </div>

        <div className="medications-section">
          <div className="section-header">
            <h3>Progress Notes</h3>
            <button 
              type="button" 
              className={`edit-toggle-btn ${editProgressNotes ? 'active' : ''}`}
              onClick={() => setEditProgressNotes(!editProgressNotes)}
              title={editProgressNotes ? "Disable edit mode" : "Enable edit mode"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
              </svg>
            </button>
          </div>
          {progressNotes.map((progressNote, index) => (
            <div key={index} className="medication-item">
              <div className="progress-note-fields">
                {editProgressNotes || !patient?.progress_notes || !patient.progress_notes[index] ? (
                  <textarea
                    placeholder="Content"
                    value={progressNote.content}
                    onChange={(e) => handleProgressNoteChange(index, 'content', e.target.value)}
                  />
                ) : (
                  <div className="progress-note-content">{progressNote.content}</div>
                )}
                {editProgressNotes || !patient?.progress_notes || !patient.progress_notes[index] ? (
                  <input
                    type="date"
                    placeholder="Date"
                    value={progressNote.date}
                    onChange={(e) => handleProgressNoteChange(index, 'date', e.target.value)}
                  />
                ) : (
                  <div className="progress-note-date">
                    {progressNote.date ? new Date(progressNote.date).toLocaleDateString('en-CA') : ''}
                  </div>
                )}
                {(editProgressNotes || !patient?.progress_notes || !patient.progress_notes[index]) && (
                  <button 
                    type="button" 
                    className="remove-medication-btn"
                    onClick={() => handleRemoveProgressNote(index)}
                    title="Remove progress note"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
          <button 
            type="button" 
            className="add-medication-btn"
            onClick={handleAddProgressNote}
          >
            Add Progress Note
          </button>
        </div>
        
        <div className="form-actions">
          <div className="form-actions-left">
            {isEdit && (
              <button 
                type="button" 
                onClick={handleDeleteClick} 
                className="delete-btn"
                disabled={loading}
              >
                Delete Patient
              </button>
            )}
          </div>
          <div className="form-actions-right">
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Update Patient' : 'Save Patient'}
            </button>
          </div>
        </div>
        
        {showDeleteConfirm && (
          <div className="delete-confirmation-modal">
            <div className="delete-confirmation-content">
              <h3>Delete Patient</h3>
              <p>Are you sure you want to delete {firstName} {lastName}?</p>
              <p>This action cannot be undone.</p>
              <div className="delete-confirmation-actions">
                <button 
                  type="button" 
                  onClick={handleDeleteCancel} 
                  className="cancel-delete-btn"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={handleDeleteConfirm} 
                  className="confirm-delete-btn"
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default PatientForm;
